import axios from "axios";
import jwtDecode from "jwt-decode";
import { toast } from "react-toastify";
import { notifyAuthSessionUpdated } from "./authSession";
import {
  buildIdentityLoginUrl,
  redirectToIdentityLogin,
} from "./identityLogin";
import { rememberAuthReturnPath } from "./walkInBookingDraft";

const ACCESS_KEYS = ["sundorbonToken", "token"];
const REFRESH_KEY = "sundorbonRefreshToken";
const USER_DETAILS_KEY = "sundorbonBookingUserDetails";

let logoutInProgress = false;
let refreshPromise = null;
let silentReauthPromise = null;
let silentReauthInProgress = false;
const SILENT_AUTH_AT_KEY = "sundorbonSilentAuthAt";
export const SILENT_AUTH_MESSAGE_TYPE = "sundorbon-silent-auth";
export const SILENT_AUTH_CALLBACK_PATH = "/auth/silent-callback";

export function getAccessToken() {
  return (
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken") ||
    ""
  );
}

export function getRefreshToken() {
  return (
    localStorage.getItem(REFRESH_KEY) ||
    sessionStorage.getItem(REFRESH_KEY) ||
    ""
  );
}

export function setAccessToken(token) {
  const value = String(token || "").trim();
  if (!value) return;
  ACCESS_KEYS.forEach((key) => localStorage.setItem(key, value));
}

export function setRefreshToken(token) {
  const value = String(token || "").trim();
  if (!value) return;
  localStorage.setItem(REFRESH_KEY, value);
}

export function applyUserDetailsFromToken(token) {
  try {
    const decoded = jwtDecode(token);
    const roleClaim =
      decoded.RoleName ||
      decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
    const roles = Array.isArray(roleClaim)
      ? roleClaim
      : [roleClaim].filter(Boolean);
    const primaryRole =
      roles.find((r) => String(r).toLowerCase() === "admin") ||
      roles.find((r) => String(r).toLowerCase() === "agent") ||
      roles[0];
    const userDetails = {
      Id: decoded.UserId,
      Name: decoded.Username || decoded.UserName,
      Type: primaryRole,
      Email: decoded.sub || decoded.UserEmail,
    };
    localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(userDetails));
    notifyAuthSessionUpdated();
    return userDetails;
  } catch {
    return null;
  }
}

function looksLikeJwt(value) {
  const raw = String(value || "").trim();
  return raw.split(".").length === 3 && raw.length > 40;
}

function extractTokenFromParams(params) {
  const direct =
    params.get("token") ||
    params.get("access_token") ||
    params.get("accessToken");
  if (looksLikeJwt(direct) || direct) return direct || "";
  for (const [key, value] of params.entries()) {
    if (/token/i.test(key) && looksLikeJwt(value)) return value;
    if (looksLikeJwt(value)) return value;
  }
  return "";
}

export function stripDuplicateDashboardPath(pathname = "") {
  const path = String(pathname || "");
  const cleaned = path.replace(
    /^(\/(?:admin-dashboard|agent-dashboard)(?:\/[\w\-./]*)?)\/(?:admin-dashboard|agent-dashboard)\/?$/i,
    "$1",
  );
  return cleaned || path;
}

export function captureAuthFromSearch(search) {
  const params = new URLSearchParams(
    typeof search === "string" ? String(search).replace(/^\?/, "") : search || "",
  );
  const token = extractTokenFromParams(params);
  const refresh =
    params.get("refreshToken") ||
    params.get("refresh_token") ||
    params.get("RefreshToken");
  if (token) {
    setAccessToken(token);
    applyUserDetailsFromToken(token);
    try {
      sessionStorage.removeItem(SILENT_AUTH_AT_KEY);
    } catch {
      /* ignore */
    }
    silentReauthInProgress = false;
  }
  if (refresh) setRefreshToken(refresh);
  return { token: token || "", refreshToken: refresh || "", params };
}

export function consumeAuthFromLocation(pathname, search) {
  const { token, refreshToken, params } = captureAuthFromSearch(search);
  const cleaned = new URLSearchParams(params);
  [
    "token",
    "access_token",
    "accessToken",
    "refreshToken",
    "refresh_token",
    "RefreshToken",
  ].forEach((key) => cleaned.delete(key));
  for (const key of [...cleaned.keys()]) {
    if (/token/i.test(key) && looksLikeJwt(cleaned.get(key))) {
      cleaned.delete(key);
    }
  }
  const nextPath = stripDuplicateDashboardPath(pathname);
  const nextSearch = cleaned.toString();
  return {
    token,
    refreshToken,
    nextPath,
    nextSearch,
    shouldReplace: Boolean(token || refreshToken) || nextPath !== pathname,
  };
}

export function persistAuthFromLoginPayload(payload = {}) {
  const token =
    payload.token ||
    payload.accessToken ||
    payload.access_token ||
    payload.Token;
  const refresh =
    payload.refreshToken ||
    payload.refresh_token ||
    payload.RefreshToken;
  if (token) {
    setAccessToken(token);
    applyUserDetailsFromToken(token);
  }
  if (refresh) setRefreshToken(refresh);
  return { token: token || "", refreshToken: refresh || "" };
}

export function clearAuthStorage() {
  ACCESS_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  localStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem("Sundarbantoken");
  localStorage.removeItem(USER_DETAILS_KEY);
}

export function isAccessTokenExpired(token = getAccessToken(), skewSeconds = 20) {
  const value = String(token || "").trim();
  if (!value) return true;
  try {
    const { exp } = jwtDecode(value);
    if (!exp) return false;
    return Date.now() >= (Number(exp) - skewSeconds) * 1000;
  } catch {
    return true;
  }
}

export function isAuthenticationError(err) {
  if (!err) return false;
  if (err.__authHandled) return true;
  const status = err?.response?.status;
  if (status === 401) return true;
  const data = err?.response?.data;
  const text = [
    typeof data === "string" ? data : "",
    data?.title,
    data?.Title,
    data?.message,
    data?.Message,
    data?.detail,
    data?.Detail,
    err?.message,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return (
    text.includes("authentication is required") ||
    text.includes("not authenticated") ||
    text.includes("unauthorized") ||
    text.includes("invalid token") ||
    text.includes("jwt expired") ||
    text.includes("token expired")
  );
}

function apiBaseUrl() {
  return process.env.REACT_APP_API_URL || "";
}

function identityOrigin() {
  const login =
    process.env.REACT_APP_IDENTITY_LOGIN_URL ||
    "https://localhost:7219/Identity/Account/Login";
  try {
    return new URL(login).origin;
  } catch {
    return "";
  }
}

function refreshEndpoints() {
  const configured = String(
    process.env.REACT_APP_TOKEN_REFRESH_URL || "",
  ).trim();
  const api = apiBaseUrl().replace(/\/?$/, "/");
  const origin = identityOrigin();
  return [
    configured,
    `${api}Auth/RefreshToken`,
    `${api}UserLogin/RefreshToken`,
    origin ? `${origin}/api/Auth/RefreshToken` : "",
    origin ? `${origin}/api/Token/Refresh` : "",
  ].filter(Boolean);
}

function pickTokenFromRefreshResponse(data) {
  if (!data) return { token: "", refreshToken: "" };
  if (typeof data === "string" && data.split(".").length === 3) {
    return { token: data, refreshToken: "" };
  }
  return {
    token:
      data.token ||
      data.accessToken ||
      data.access_token ||
      data.Token ||
      data.jwt ||
      "",
    refreshToken:
      data.refreshToken ||
      data.refresh_token ||
      data.RefreshToken ||
      "",
  };
}

async function postRefresh(url, body) {
  const { data, status } = await axios.post(url, body, {
    skipAuthRefresh: true,
    withCredentials: url.includes(identityOrigin()) && !!identityOrigin(),
    validateStatus: (s) => s < 500,
    headers: { "Content-Type": "application/json", accept: "*/*" },
  });
  if (status === 404 || status === 405) return null;
  if (status >= 400) return null;
  const picked = pickTokenFromRefreshResponse(data);
  if (!picked.token) return null;
  return picked;
}

export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  const accessToken = getAccessToken();
  if (!refreshToken) return null;

  const body = {
    refreshToken,
    accessToken,
    token: accessToken,
  };

  for (const url of refreshEndpoints()) {
    try {
      const picked = await postRefresh(url, body);
      if (!picked?.token) continue;
      setAccessToken(picked.token);
      if (picked.refreshToken) setRefreshToken(picked.refreshToken);
      applyUserDetailsFromToken(picked.token);
      return picked.token;
    } catch {
      // try next endpoint
    }
  }
  return null;
}

function refreshAccessTokenOnce() {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function ensureFreshAccessToken() {
  const token = getAccessToken();
  if (token && !isAccessTokenExpired(token)) return token;
  if (getRefreshToken()) {
    const refreshed = await refreshAccessTokenOnce();
    if (refreshed && !isAccessTokenExpired(refreshed)) return refreshed;
  }
  const current = getAccessToken();
  if (current && !isAccessTokenExpired(current)) return current;
  const silent = await beginSilentIdentityReauth();
  return silent || "";
}

function currentReturnUrl() {
  if (typeof window === "undefined") return undefined;
  const path = stripDuplicateDashboardPath(window.location.pathname);
  const params = new URLSearchParams(window.location.search);
  ["token", "access_token", "accessToken"].forEach((key) => params.delete(key));
  const search = params.toString();
  return `${window.location.origin}${path}${search ? `?${search}` : ""}`;
}

function isSilentAuthFrame() {
  if (typeof window === "undefined") return false;
  if (window.location.pathname.startsWith(SILENT_AUTH_CALLBACK_PATH)) return true;
  return Boolean(window.parent && window.parent !== window);
}

function identityLoginOrigin() {
  try {
    return new URL(
      process.env.REACT_APP_IDENTITY_LOGIN_URL ||
        "https://localhost:7219/Identity/Account/Login",
    ).origin;
  } catch {
    return "";
  }
}

function isTrustedSilentAuthOrigin(origin) {
  if (!origin) return false;
  if (origin === window.location.origin) return true;
  const identity = identityLoginOrigin();
  return Boolean(identity) && origin === identity;
}

function silentCallbackUrl() {
  return `${window.location.origin}${SILENT_AUTH_CALLBACK_PATH}`;
}

function silentIdentityLoginUrl() {
  return `${buildIdentityLoginUrl(silentCallbackUrl())}&silent=1`;
}

function applySilentToken(token) {
  const value = String(token || "").trim();
  if (!value) return "";
  setAccessToken(value);
  applyUserDetailsFromToken(value);
  try {
    sessionStorage.removeItem(SILENT_AUTH_AT_KEY);
  } catch {
    /* ignore */
  }
  silentReauthInProgress = false;
  return value;
}

function waitForSilentAuthMessage(timeoutMs) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (token) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
      resolve(token || "");
    };
    const onMessage = (event) => {
      if (!isTrustedSilentAuthOrigin(event.origin)) return;
      const data = event.data || {};
      if (data.type !== SILENT_AUTH_MESSAGE_TYPE) return;
      finish(applySilentToken(data.token));
    };
    const timer = window.setTimeout(() => finish(""), timeoutMs);
    window.addEventListener("message", onMessage);
  });
}

function tryIframeSilentAuth(loginUrl, timeoutMs = 3500) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.name = "sundorbon-silent-auth";
    iframe.setAttribute("title", "Session refresh");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText =
      "position:fixed;width:0;height:0;opacity:0;border:0;left:0;top:0;pointer-events:none";
    const wait = waitForSilentAuthMessage(timeoutMs).finally(() => {
      try {
        iframe.remove();
      } catch {
        /* ignore */
      }
    });
    iframe.src = loginUrl;
    document.body.appendChild(iframe);
    wait.then(resolve);
  });
}

async function runSilentIdentityReauth() {
  if (typeof window === "undefined" || logoutInProgress || isSilentAuthFrame()) {
    return "";
  }

  rememberAuthReturnPath(
    `${window.location.pathname}${window.location.search || ""}`,
  );
  const loginUrl = silentIdentityLoginUrl();

  const iframeToken = await tryIframeSilentAuth(loginUrl);
  if (iframeToken) return iframeToken;

  // Top-level login must use the real booking URL, not /auth/silent-callback.
  // A silent-callback returnUrl is served as a blank page and never comes back.
  redirectToIdentityLogin(currentReturnUrl());
  return "";
}

export function beginSilentIdentityReauth() {
  if (typeof window === "undefined" || logoutInProgress) {
    return Promise.resolve("");
  }
  if (silentReauthPromise) return silentReauthPromise;

  let last = 0;
  try {
    last = Number(sessionStorage.getItem(SILENT_AUTH_AT_KEY) || 0);
  } catch {
    last = 0;
  }
  if (last && Date.now() - last < 15000 && silentReauthInProgress) {
    return silentReauthPromise || Promise.resolve("");
  }

  silentReauthInProgress = true;
  try {
    sessionStorage.setItem(SILENT_AUTH_AT_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }

  silentReauthPromise = runSilentIdentityReauth().finally(() => {
    silentReauthPromise = null;
    silentReauthInProgress = false;
  });
  return silentReauthPromise;
}

export function forceLogout(reason = "session") {
  if (logoutInProgress) return;
  logoutInProgress = true;
  clearAuthStorage();
  notifyAuthSessionUpdated();
  toast.error(
    reason === "missing"
      ? "Please sign in again."
      : "Session expired. Please sign in again.",
    { toastId: "auth-session-expired", autoClose: 4000 },
  );
  redirectToIdentityLogin(currentReturnUrl());
}

export function bindAuthInterceptor() {
  if (bindAuthInterceptor.bound) return;
  bindAuthInterceptor.bound = true;

  axios.interceptors.request.use(async (config) => {
    if (config.skipAuthRefresh || isSilentAuthFrame()) return config;
    const headers = config.headers || {};
    let token = getAccessToken();
    if (token && isAccessTokenExpired(token) && getRefreshToken()) {
      token = (await refreshAccessTokenOnce()) || token;
    }
    if (!token || isAccessTokenExpired(token)) {
      const silent = await beginSilentIdentityReauth();
      if (silent) token = silent;
    }
    token = getAccessToken() || token;
    if (token && !isAccessTokenExpired(token)) {
      headers.Authorization = `Bearer ${token}`;
      config.headers = headers;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config || {};
      if (config.skipAuthRefresh || config.__authRetry || isSilentAuthFrame()) {
        return Promise.reject(error);
      }
      if (!isAuthenticationError(error)) {
        return Promise.reject(error);
      }

      if (getRefreshToken()) {
        const newToken = await refreshAccessTokenOnce();
        if (newToken) {
          config.__authRetry = true;
          config.headers = {
            ...(config.headers || {}),
            Authorization: `Bearer ${newToken}`,
          };
          return axios(config);
        }
      }

      const silent = await beginSilentIdentityReauth();
      if (silent) {
        config.__authRetry = true;
        config.headers = {
          ...(config.headers || {}),
          Authorization: `Bearer ${silent}`,
        };
        return axios(config);
      }

      error.__authHandled = true;
      return Promise.reject(error);
    },
  );
}

bindAuthInterceptor.bound = false;
